import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FilePen, Check, ArrowDownToLine } from "lucide-react";
import { ImportCaseStudyForm } from "../ImportCaseStudyModal";
import { CaseStudyItem } from "@/types/case-study";

interface CaseStudyCreationModalProps {
  open: boolean;
  onClose: () => void;
  onStartFromScratch: () => void;
  onStartWithImportedData?: (data: Partial<CaseStudyItem>) => void;
}

type CreationOption = "scratch" | "import" | null;

export const CaseStudyCreationModal: React.FC<CaseStudyCreationModalProps> = ({
  open,
  onClose,
  onStartFromScratch,
  onStartWithImportedData,
}) => {
  const [selectedOption, setSelectedOption] = useState<CreationOption>(null);
  const [currentStep, setCurrentStep] = useState<"select" | "import">("select");

  const handleOptionSelect = (option: CreationOption) => {
    setSelectedOption(option);
    // Immediately execute the action when option is selected
    if (option === "scratch") {
      onStartFromScratch();
    } else if (option === "import") {
      setCurrentStep("import");
    }
  };

  const handleClose = () => {
    setSelectedOption(null);
    setCurrentStep("select");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-white rounded-lg sm:max-w-[800px] px-20 py-10">
        {currentStep === "import" ? (
          <ImportCaseStudyForm
            onBack={() => {
              setCurrentStep("select");
              setSelectedOption(null);
            }}
            onImport={(url) => {

            }}
            onImportSuccess={(data) => {
              if (onStartWithImportedData) {
                onStartWithImportedData(data);
              }
              handleClose();
            }}
          />
        ) : (
          <>
            <DialogHeader className="px-6 pt-6 pb-4">
              <DialogTitle className="text-3xl font-bold text-center">
                Build your Case Study
              </DialogTitle>
              <p className="text-gray-600 text-center mt-2">
                Select how you would like to build your case study.
              </p>
            </DialogHeader>

            <div className="px-6 pb-6">
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleOptionSelect("scratch")}
                  className={`relative flex flex-col items-center justify-between p-6 border-2 rounded-lg transition-all group ${
                    selectedOption === "scratch"
                      ? "border-orange-500 bg-orange-50 shadow-lg"
                      : "border-gray-200 hover:border-orange-500 hover:bg-orange-50 hover:shadow-lg"
                  }`}
                >
                  <div className="flex items-center justify-center w-12 h-12 mb-4 bg-[#FFEDD5] rounded-full">
                    <FilePen className="w-6 h-6 text-orange-500" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Start from Scratch</h3>
                  <p className="text-sm text-gray-600 text-center leading-relaxed">
                    Build your case studies from scratch using our Case Studies tool.
                  </p>
                  {selectedOption === "scratch" && (
                    <div className="absolute top-3 right-3 w-5 h-5 bg-[#D0EDDB] border border-green-500 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-green-500" />
                    </div>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => handleOptionSelect("import")}
                  className={`relative flex flex-col items-center justify-between p-6 border-2 rounded-lg transition-all group ${
                    selectedOption === "import"
                      ? "border-orange-500 bg-orange-50 shadow-lg"
                      : "border-gray-200 hover:border-orange-500 hover:bg-orange-50 hover:shadow-lg"
                  }`}
                >
                  <div className="flex items-center justify-center w-12 h-12 mb-4 bg-[#FFEDD5] rounded-full">
                    <ArrowDownToLine className="w-6 h-6 text-orange-500" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Import Case Study</h3>
                  <p className="text-sm text-gray-600 text-center leading-relaxed">
                    Import a case study from your personal portfolio page.
                  </p>
                  {selectedOption === "import" && (
                    <div className="absolute top-3 right-3 w-5 h-5 bg-[#D0EDDB] border border-green-500 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-green-500" />
                    </div>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
